import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import axios from 'axios';
import {
  Building2,
  Mail,
  Lock,
  User,
  Phone,
  MapPin,
  FileText,
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Upload,
  X,
  Check,
  Loader2,
  Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerLogin = () => {
  const navigate = useNavigate();
  const { login, partner } = usePartnerAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // PWA Install prompt state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  // Registration steps: 'form' -> 'documents' -> 'success'
  const [registrationStep, setRegistrationStep] = useState('form');
  const [registeredPartnerId, setRegisteredPartnerId] = useState(null);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    contact_person: '',
    phone: '',
    business_type: '',
    gst_number: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  // PWA Install prompt listener
  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (!isStandalone) {
      setShowInstallBanner(true);
    }
    
    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle PWA install
  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    
    if (!installPrompt) {
      toast.info('To install, use your browser menu and select "Add to Home Screen"');
      return;
    }
    
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
        setShowInstallBanner(false);
      }
      setInstallPrompt(null);
    } catch (error) {
      console.error('Install error:', error);
    }
  };

  useEffect(() => {
    if (partner) {
      navigate('/partner/dashboard');
    }
  }, [partner, navigate]);

  useEffect(() => {
    fetchDocumentRequirements();
  }, []);

  const fetchDocumentRequirements = async () => {
    try {
      const response = await axios.get(`${API}/partner/document-requirements`);
      setDocumentRequirements(response.data);
    } catch (error) {
      console.error('Failed to fetch document requirements:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/partner/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/partner/register`, {
        company_name: formData.company_name,
        contact_person: formData.contact_person,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        business_type: formData.business_type,
        gst_number: formData.gst_number,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode
      });

      setRegisteredPartnerId(response.data.partner_id);
      
      // Check if there are document requirements
      const requiredDocs = documentRequirements.filter(d => d.is_required);
      if (requiredDocs.length > 0) {
        setRegistrationStep('documents');
        toast.success('Registration successful! Please upload required documents.');
      } else {
        setRegistrationStep('success');
        toast.success('Registration successful!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (documentKey, file) => {
    if (!file || !registeredPartnerId) return;
    
    setUploadingDoc(documentKey);
    
    const formData = new FormData();
    formData.append('document_type', documentKey);
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/partner/register/${registeredPartnerId}/documents`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setUploadedDocuments(prev => ({
        ...prev,
        [documentKey]: {
          fileName: file.name,
          status: 'uploaded',
          documentId: response.data.document_id
        }
      }));
      
      toast.success('Document uploaded successfully');
      
      // Check if all required documents are uploaded
      if (response.data.all_required_uploaded) {
        toast.success('All required documents uploaded! Your application is under review.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleCompleteRegistration = () => {
    // Check if all required documents are uploaded
    const requiredDocs = documentRequirements.filter(d => d.is_required);
    const allRequiredUploaded = requiredDocs.every(d => uploadedDocuments[d.document_key]);
    
    if (!allRequiredUploaded) {
      toast.error('Please upload all required documents');
      return;
    }
    
    setRegistrationStep('success');
  };

  // Success Screen
  if (registrationStep === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-green-600" size={40} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h2>
          <p className="text-gray-600 mb-6">
            Your partner application has been submitted successfully. Our team will review your documents and get back to you shortly.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-blue-700">
              <strong>What's next?</strong>
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• Our team will review your documents</li>
              <li>• You'll receive an email once approved</li>
              <li>• Then you can login and start submitting leads</li>
            </ul>
          </div>
          <Button onClick={() => { setRegistrationStep('form'); setIsLogin(true); }} className="w-full">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  // Document Upload Screen
  if (registrationStep === 'documents') {
    const requiredDocs = documentRequirements.filter(d => d.is_required);
    const optionalDocs = documentRequirements.filter(d => !d.is_required);
    const allRequiredUploaded = requiredDocs.every(d => uploadedDocuments[d.document_key]);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="text-blue-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Upload Documents</h2>
            <p className="text-gray-600 mt-2">Please upload the required documents to complete your registration</p>
          </div>

          {/* Required Documents */}
          {requiredDocs.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <AlertCircle className="text-red-500" size={18} />
                Required Documents
              </h3>
              <div className="space-y-3">
                {requiredDocs.map((doc) => (
                  <DocumentUploadCard
                    key={doc.document_key}
                    document={doc}
                    uploaded={uploadedDocuments[doc.document_key]}
                    uploading={uploadingDoc === doc.document_key}
                    onUpload={(file) => handleDocumentUpload(doc.document_key, file)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Optional Documents */}
          {optionalDocs.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Optional Documents</h3>
              <div className="space-y-3">
                {optionalDocs.map((doc) => (
                  <DocumentUploadCard
                    key={doc.document_key}
                    document={doc}
                    uploaded={uploadedDocuments[doc.document_key]}
                    uploading={uploadingDoc === doc.document_key}
                    onUpload={(file) => handleDocumentUpload(doc.document_key, file)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <Button 
              variant="outline" 
              onClick={() => setRegistrationStep('success')}
              className="flex-1"
            >
              Skip for Now
            </Button>
            <Button 
              onClick={handleCompleteRegistration}
              disabled={!allRequiredUploaded}
              className="flex-1"
            >
              {allRequiredUploaded ? 'Complete Registration' : 'Upload Required Docs'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Login/Register Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full flex flex-col md:flex-row">
        {/* Left Panel - Branding */}
        <div className="bg-gradient-to-br from-blue-700 to-purple-700 p-8 md:p-12 md:w-2/5 text-white">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Partner Portal</h1>
            <p className="text-blue-100">Visaasure Business Partners</p>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Building2 size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Grow Your Business</h3>
                <p className="text-sm text-blue-100">Partner with us and earn commissions on every successful referral</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Eye size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Real-time Tracking</h3>
                <p className="text-sm text-blue-100">Track all your leads and their progress in real-time</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Easy Payouts</h3>
                <p className="text-sm text-blue-100">Transparent commission structure with timely payouts</p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/20 space-y-4">
            <Link to="/" className="text-sm text-blue-100 hover:text-white transition-colors block">
              ← Back to Main Website
            </Link>
            
            {/* Install App Button */}
            {showInstallBanner && (
              <button
                onClick={handleInstallClick}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all"
              >
                <Smartphone size={18} />
                <span>Install App</span>
              </button>
            )}
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="p-8 md:p-12 md:w-3/5 max-h-[90vh] overflow-y-auto">
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                isLogin
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                !isLogin
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Register
            </button>
          </div>

          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Welcome Back</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="partner@company.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Become a Partner</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="Your Company"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="Your Name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="partner@company.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="Min 8 characters"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="Confirm password"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                  <Input
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleChange}
                    placeholder="Travel Agency, Corporate, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                  <Input
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleChange}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="pl-10"
                      placeholder="Street address"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showPwd"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="showPwd" className="text-sm text-gray-600">Show password</label>
              </div>

              {documentRequirements.filter(d => d.is_required).length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle size={16} />
                    <span className="font-medium">Documents Required</span>
                  </div>
                  <p>You'll need to upload the following documents after registration:</p>
                  <ul className="list-disc list-inside mt-1 text-xs">
                    {documentRequirements.filter(d => d.is_required).map(doc => (
                      <li key={doc.document_key}>{doc.document_name}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Registering...' : 'Continue to Document Upload'}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Install on iPhone/iPad</h3>
              <button 
                onClick={() => setShowIOSInstructions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-blue-600">1</span>
                </div>
                <p>Tap the <strong>Share</strong> button <span className="inline-block w-5 h-5 bg-gray-200 rounded text-center text-xs leading-5">↑</span> at the bottom of Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-blue-600">2</span>
                </div>
                <p>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-blue-600">3</span>
                </div>
                <p>Tap <strong>&quot;Add&quot;</strong> in the top right corner</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6"
            >
              Got it!
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Document Upload Card Component
const DocumentUploadCard = ({ document, uploaded, uploading, onUpload }) => {
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${uploaded ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${uploaded ? 'bg-green-100' : 'bg-gray-200'}`}>
            {uploaded ? (
              <Check className="text-green-600" size={20} />
            ) : (
              <FileText className="text-gray-500" size={20} />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">{document.document_name}</p>
            {document.description && (
              <p className="text-xs text-gray-500">{document.description}</p>
            )}
            {uploaded && (
              <p className="text-xs text-green-600 mt-1">✓ {uploaded.fileName}</p>
            )}
            <p className="text-xs text-gray-400">
              Formats: {document.allowed_formats?.join(', ') || 'pdf, jpg, png'} | Max: {document.max_size_mb || 5}MB
            </p>
          </div>
        </div>
        <div>
          {uploading ? (
            <Loader2 className="animate-spin text-blue-600" size={24} />
          ) : (
            <label className="cursor-pointer">
              <input
                type="file"
                className="hidden"
                accept={document.allowed_formats?.map(f => `.${f}`).join(',') || '.pdf,.jpg,.jpeg,.png'}
                onChange={handleFileSelect}
              />
              <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                uploaded 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                {uploaded ? 'Replace' : 'Upload'}
              </div>
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
